import { useGetInvestmentFundsQuery, useGetRolesQuery } from 'api/main';
import { useCheckVerificationCodeMutation } from 'api/tunnelSteps';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoutes, useLocation, useMatch } from 'react-router-dom';
import { tunnelSelectors, userSelectors } from 'reducers';
import { currentInvestmentFundSet } from 'reducers/tunnel';
import { LoggingRedirect } from 'routes/LoggingRedirect';
import { paymentMethods, stepIDs } from 'utils/constants';
import { useRoleChoice } from 'utils/hooks/subscriptions';
import {
    useAllStepIds,
    useAllTunnelStepIds,
    useGetReferralParameters,
    useTunnelPhases,
} from 'utils/hooks/tunnel';
import { TUNNEL_STEPS } from 'containers/tunnel/tunnelSteps';

const routes = [
    { path: '/sign-up/:stepId' },
    { path: '/sign-up/:stepId/:tsId' },
    { path: '/new-subscription/:stepId' },
    { path: '/new-subscription/:stepId/:tsId' },
    { path: '/onboarding/:stepId' },
    { path: '/create-subscription/:stepId' },
];

const withTunnel = Component => () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    // use matchRoutes to extract the params from the wildcard path
    const matches = matchRoutes(routes, pathname);
    const stepId = +matches?.at(-1)?.params?.stepId;
    const tsId = +matches?.at(-1)?.params?.tsId;

    const { product } = useGetReferralParameters();

    // api hooks
    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: fundList = [] } = useGetInvestmentFundsQuery({ withPrivate: 1 });
    const [, { isLoading: codeLoader }] = useCheckVerificationCodeMutation({
        fixedCacheKey: 'code-check',
    });

    // redux store data
    const loggedIn = useSelector(userSelectors.getLoggedInStatus);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const userSteps = useSelector(userSelectors.getUserTunnelSteps);
    const roleChoice = useRoleChoice();
    const { id: fundId, name: fundName } = useSelector(tunnelSelectors.getCurrentInvestmentFund);

    // state items
    const investmentFund = tunnelSubscription?.investmentFund;
    const [roleSubmitted, setRoleSubmitted] = useState(
        isCreatingSubscription ? false : !!tunnelSubscription?.role.name
    );
    const [fundSubmitted, setFundSubmitted] = useState(
        isCreatingSubscription ? !!product : !!investmentFund?.id || false
    );
    const [submittedPedagogicalStep, setSubmittedPedagogicalStep] = useState(
        isCreatingSubscription ? false : !!tunnelSubscription
    );

    // ---- Memoized values ----
    const roleName =
        loggedIn && !isCreatingSubscription ? tunnelSubscription?.role?.name : roleChoice;
    const roleId = useMemo(() => roles.find(r => r.name === roleName)?.id, [roles, roleName]);
    const { phases, isLoading: tunnelListLoader } = useTunnelPhases();

    // current phase computed using stepId
    const phaseMemo = useMemo(
        () =>
            loggedIn && !isCreatingSubscription && stepId
                ? phases.find(ph => ph.tunnelSteps.find(ts => ts.step.id === stepId))
                : phases[0],
        [loggedIn, phases, stepId, isCreatingSubscription]
    );

    // this is the current step that the user is on (using stepId in the URL)
    const tunnelStepMemo = useMemo(() => {
        return loggedIn && !isCreatingSubscription && stepId
            ? phaseMemo?.tunnelSteps.find(
                  ts => ts.step.id === stepId && (tsId ? ts.id === tsId : true)
              )
            : phaseMemo?.tunnelSteps[0];
    }, [phaseMemo, stepId, tsId, loggedIn, isCreatingSubscription]);

    const allStepIds = useAllStepIds();
    const allTunnelStepIds = useAllTunnelStepIds();

    // computes the last step before the first gap in the userTunnelSteps
    // first it finds the corresponding userTunnelStep[i] in allTunnelStepIds and then compares the next step in both lists
    // if they don't match there is a gap between userTunnelStep[i] and userTunnelStep[i+1] and thus returns userTunnelStep[i]
    const lastStepBeforeGap = useMemo(() => {
        let userTunnelStepIds = userSteps.map(us => us.tunnelStep.id);

        for (let i = 0; i < userTunnelStepIds.length - 1; i++) {
            const index = allTunnelStepIds.findIndex(sId => sId === userTunnelStepIds[i]);
            if (
                index > -1 &&
                index < allTunnelStepIds.length - 1 &&
                allTunnelStepIds[index + 1] !== userTunnelStepIds[i + 1] &&
                allTunnelStepIds.includes(userTunnelStepIds[i + 1])
            ) {
                return userSteps.find(us => us.tunnelStep.id === userTunnelStepIds[i]);
            }
        }

        return null;
    }, [userSteps, allTunnelStepIds]);

    const firstFailedUserStep = useMemo(
        () =>
            userSteps.find(us => {
                let localTunnelStep = TUNNEL_STEPS.find(ts => ts.stepId === us.tunnelStep.step.id);
                return localTunnelStep ? !localTunnelStep.allowNext.includes(us.status) : false;
            }),
        [userSteps]
    );

    // This method returns either firstFailedUserStep or lastStepBeforeGap
    // if either one of these is not null, it returns the other
    // if neither is null it needs to find at which of these steps to stop first
    // so it computes the indexes of both these steps and return the step with lower index
    const firstUncommonStep = useMemo(() => {
        if (lastStepBeforeGap && !firstFailedUserStep) {
            return lastStepBeforeGap;
        } else if (!lastStepBeforeGap && firstFailedUserStep) {
            return firstFailedUserStep;
        } else if (!lastStepBeforeGap && !firstFailedUserStep) {
            return null;
        } else {
            const lastStepBeforeGapIndex = allStepIds.findIndex(
                el => el === lastStepBeforeGap?.tunnelStep.step.id
            );
            const firstFailedUserStepIndex = allStepIds.findIndex(
                el => el === firstFailedUserStep?.tunnelStep.step.id
            );
            if (lastStepBeforeGapIndex > -1 && firstFailedUserStepIndex > -1) {
                let lastStep = null;
                if (lastStepBeforeGapIndex < firstFailedUserStepIndex) {
                    lastStep = lastStepBeforeGap;
                } else {
                    lastStep = firstFailedUserStep;
                }
                return lastStep;
            } else if (lastStepBeforeGapIndex === -1) {
                return firstFailedUserStep;
            } else {
                return lastStepBeforeGap;
            }
        }
    }, [allStepIds, firstFailedUserStep, lastStepBeforeGap]);

    // the first failedTunnelStepId OR the last tunnelStepId that the user has accessed
    const lastTunnelStepId = useMemo(() => {
        if (loggedIn && userSteps.length) {
            const { tunnelStep } = userSteps[userSteps.length - 1];
            const lastTunnelStep = firstUncommonStep?.tunnelStep || tunnelStep;
            const lastStepId = lastTunnelStep?.step?.id;
            // if the current step is unavailable then search starting from the last the first one available
            if (!allStepIds.includes(lastStepId)) {
                const lastUserStepAvailable = [...userSteps]
                    .reverse()
                    .find(us => allStepIds.includes(us?.tunnelStep?.step?.id));
                return lastUserStepAvailable?.tunnelStep?.id || userSteps[0]?.tunnelStep?.id;
            }
            return lastTunnelStep?.id;
        }
        return 0;
    }, [firstUncommonStep, userSteps, loggedIn, allStepIds]);

    // this is the last accessible phase in the tunnel
    const lastPhaseMemo = useMemo(() => {
        if (!phases.length || codeLoader) return null;
        if (loggedIn) {
            const { status } = userSteps.find(us => us.tunnelStep.id === lastTunnelStepId) || {};
            const tunnelPhaseIndex = phases.findIndex(ph =>
                ph.tunnelSteps.find(ts => ts.id === lastTunnelStepId)
            );

            // if it's the last phase just return it
            if (tunnelPhaseIndex === phases.length - 1) return phases[tunnelPhaseIndex];

            if (tunnelPhaseIndex >= 0) {
                const lastStepInPhase =
                    phases[tunnelPhaseIndex].tunnelSteps[
                        phases[tunnelPhaseIndex].tunnelSteps.length - 1
                    ];
                const isLastStepInPhase = lastStepInPhase.id === lastTunnelStepId;
                const localStep = TUNNEL_STEPS.find(ts => ts.stepId === lastStepInPhase.step.id);

                let isNextStepAvailable = localStep.allowNext.includes(status);
                if (isLastStepInPhase) {
                    const firstStepInNextPhase = phases[tunnelPhaseIndex + 1].tunnelSteps.filter(
                        ts => allStepIds.includes(ts.step.id)
                    )[0];

                    if (
                        !firstStepInNextPhase ||
                        !allStepIds.includes(firstStepInNextPhase?.step?.id)
                    ) {
                        isNextStepAvailable = false;
                    }
                }

                return isLastStepInPhase && isNextStepAvailable // can go past last step in phase and
                    ? phases[tunnelPhaseIndex + 1]
                    : phases[tunnelPhaseIndex];
            }
            return phases[0];
        } else {
            return phases[0];
        }
    }, [phases, loggedIn, userSteps, codeLoader, lastTunnelStepId, allStepIds]);

    const lastPhaseIndex = useMemo(
        () => phases.findIndex(ph => ph.id === lastPhaseMemo?.id),
        [phases, lastPhaseMemo]
    );

    // this is the last tunnel step that can be accessed by user
    const lastTunnelStepMemo = useMemo(() => {
        if (!phases.length || !lastPhaseMemo) return null;
        if (loggedIn) {
            if (!lastPhaseMemo) return null;
            const { status } = userSteps[userSteps.length - 1] || {};
            const lastTunnelStepIndex = lastPhaseMemo.tunnelSteps.findIndex(
                ts => ts.id === lastTunnelStepId
            );

            const firstUncommonStepLocal = TUNNEL_STEPS.find(
                x => x.stepId === firstUncommonStep?.tunnelStep.step.id
            );

            const currentIndexShift = firstUncommonStepLocal?.allowNext.includes(
                firstUncommonStep?.status || status
            )
                ? 1
                : 0;

            const currentIndex = firstUncommonStep
                ? lastTunnelStepIndex + currentIndexShift
                : lastTunnelStepIndex + 1;

            const originalStep =
                lastPhaseMemo.tunnelSteps[lastTunnelStepIndex === -1 ? 0 : lastTunnelStepIndex];
            const nextStep = lastPhaseMemo.tunnelSteps[currentIndex === -1 ? 0 : currentIndex];

            if (
                tunnelSubscription?.bankingStatement?.paymentMethod ===
                    paymentMethods.CREDIT_TRANSFER &&
                nextStep?.step?.id === stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE
            ) {
                return lastPhaseMemo.tunnelSteps[currentIndex + 1];
            }

            if (!nextStep) {
                return originalStep;
            }

            // return second step(assuming the first is 'register') if only that was submitted
            return lastPhaseMemo.tunnelSteps[currentIndex === -1 ? 0 : currentIndex];
        } else {
            // return the first step if not logged in
            return phases[0].tunnelSteps[0];
        }
    }, [
        phases,
        loggedIn,
        lastTunnelStepId,
        lastPhaseMemo,
        userSteps,
        firstUncommonStep,
        tunnelSubscription?.bankingStatement?.paymentMethod,
    ]);

    // ---- Side effects ----

    useEffect(() => {
        // update role submitted flag for new subscriptions(role should be preselected)
        setRoleSubmitted(!!tunnelSubscription?.role.name);
        if (tunnelSubscription) {
            // set investment fund initial value in redux
            dispatch(currentInvestmentFundSet(tunnelSubscription.investmentFund));
        }
    }, [tunnelSubscription, dispatch]);

    useEffect(() => {
        if (product && fundList.length > 0) {
            const fund = fundList.find(f => f.id === +product);
            if (fund) {
                dispatch(currentInvestmentFundSet(fund));
                setFundSubmitted(true);
            }
        } else if (investmentFund) {
            dispatch(currentInvestmentFundSet(investmentFund));
            setFundSubmitted(true);
        }
    }, [fundList, product, dispatch, investmentFund]);

    if (
        roles.length &&
        !roles.find(r => r.id === roleId) &&
        !(tunnelListLoader || rolesLoader || !roleSubmitted)
    ) {
        return <LoggingRedirect origin="tunnel_HOC" to="/sign-in" />;
    }

    return (
        <Component
            {...{
                allStepIds,
                allTunnelStepIds,
                codeLoader,
                firstFailedUserStep,
                fundId,
                fundName,
                fundSubmitted,
                isCreatingSubscription,
                lastPhaseIndex,
                lastPhaseMemo,
                lastTunnelStepMemo,
                loggedIn,
                phaseMemo,
                roleChoice,
                roleId,
                roleName,
                rolesLoader,
                roleSubmitted,
                setFundSubmitted,
                setRoleSubmitted,
                setSubmittedPedagogicalStep,
                stepId,
                submittedPedagogicalStep,
                tsId,
                tunnelStepMemo,
                tunnelSubscription,
                userSteps,
            }}
        />
    );
};

export default withTunnel;
